import type { EmployeePersona } from "@peer-start/platform-contracts";
import { Card, CardHeader, SelectField } from "@peer-start/ui";
import { SquarePen } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { demoEmail, demoUuid, formatProfileDate } from "./profile-demo.js";
import { ProfileDetailRow } from "./ProfileDetailRow.js";

export function ProfileDetailsTab({ persona }: { persona: EmployeePersona }) {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState(false);
  const email = demoEmail(persona.id);
  const languageLabel = t(i18n.language === "nl" ? "profile.languageNl" : "profile.languageEn");

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card aria-label={t("profile.personalDetails")}>
        <CardHeader
          action={
            <button
              className="inline-flex min-h-9 items-center gap-1.5 text-sm font-medium text-link transition-colors hover:underline focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
              data-control-id="profile.personal.edit"
              onClick={() => setEditing((value) => !value)}
              type="button"
            >
              <SquarePen aria-hidden="true" className="size-4" strokeWidth={2} />
              {editing ? t("common.close") : t("profile.edit")}
            </button>
          }
          title={t("profile.personalDetails")}
        />
        {editing ? (
          <div className="space-y-4">
            <ProfileDetailRow label={t("profile.fullName")} value={persona.name} />
            <SelectField
              controlId="profile.personal.language"
              label={t("common.language")}
              onChange={(event) => {
                const language = event.target.value === "nl" ? "nl" : "en";
                void i18n.changeLanguage(language);
                localStorage.setItem("peer-start-language", language);
              }}
              value={i18n.language === "nl" ? "nl" : "en"}
            >
              <option value="nl">{t("profile.languageNl")}</option>
              <option value="en">{t("profile.languageEn")}</option>
            </SelectField>
          </div>
        ) : (
          <dl>
            <ProfileDetailRow label={t("profile.fullName")} value={persona.name} />
            <ProfileDetailRow label={t("common.language")} value={languageLabel} />
          </dl>
        )}
      </Card>

      <Card aria-label={t("profile.accountDetails")}>
        <CardHeader title={t("profile.accountDetails")} />
        <dl>
          <ProfileDetailRow label={t("profile.email")} value={email} />
          <ProfileDetailRow
            label={t("profile.lastLogin")}
            value={formatProfileDate(new Date(), i18n.language)}
          />
          <ProfileDetailRow label={t("profile.userId")} value={demoUuid(persona.id, "user")} />
          <ProfileDetailRow label={t("profile.subject")} value={demoUuid(persona.id, "subject")} />
          <ProfileDetailRow label={t("profile.provider")} value="fusionauth" />
        </dl>
      </Card>
    </div>
  );
}
